import React, { useState, useEffect } from "react";
import { Row, Col, Button } from "react-bootstrap";
import Dropzone from "react-dropzone";

interface IFormDropzoneProps {
  onFileUpload: (files: File[]) => void; // Callback for file uploads
  existingFiles?: any[]; // Existing files with optional file details
  multiple?: boolean; // Allow multiple files
  deleteHandler?: (file: any) => void; // Optional delete handler
  shouldAppend?: boolean; // Flag to determine if files should be appended or replaced
  accept?: string; // MIME types or file extensions to accept
}

const FormDropzone: React.FC<IFormDropzoneProps> = ({
  onFileUpload,
  existingFiles = [],
  multiple = false,
  deleteHandler,
  shouldAppend = false, // Default to false
  accept = "*", // Default to accept all files
}) => {
  const [files, setFiles] = useState<any[]>([]);

  // Initialize files with existing files
  useEffect(() => {

    if (existingFiles?.length > 0) {
    

      const formattedFiles = existingFiles.map((file) => {

        if (file?.file) {
          // Backend data format
          return {
            id: file?.id || null,
            preview: file?.path || file?.file?.path || "", // Use the path for preview
            name: file?.file?.nameOriginal || file?.file?.name || "Unknown File", // Use original name if available
            type: file?.file?.mime?.startsWith("image/") ? "image" : "document", // Determine type based on MIME
            isExisting: true, // Mark as an existing file
          };
        } else if (file?.file?.path && file?.file?.relativePath) {
          // Newly added file format
          return {
            id: null, // No ID for newly uploaded files
            preview: file?.preview || URL.createObjectURL(file?.file), // Generate a preview URL if not already set
            name: file?.name || file?.file?.path?.split("/")?.pop() || "Unknown File", // Extract name from path
            type: file?.type || (file?.file?.type?.startsWith("image/") ? "image" : "document"), // Determine type based on MIME
            isExisting: false, // Mark as a newly uploaded file
          };
        } else if (file?.preview && file?.name) {
          // Already formatted file (e.g., from state)
          return {
            ...file,
            isExisting: true, // Ensure it's marked as an existing file
          };
        } else if (existingFiles?.[0]?.[0] instanceof File) {
          // Handle case where existingFiles contains a File object
          return {
            preview: URL.createObjectURL(existingFiles?.[0]?.[0]), // Generate a preview URL
            type: existingFiles?.[0]?.[0]?.type?.startsWith("image/") ? "image" : "document", // Determine type based on MIME
            isExisting: false, // Mark as a newly uploaded file
            file, // Store the actual file object
          };
        } else {
          // Fallback for unexpected formats
          console.warn("Unexpected file format:", file);
          return {
            id: file?.id || null,
            preview: file?.preview || "", // Use preview or fallback to empty string
            name: file?.name || "Unknown File",
            type: file?.type || "document", // Default to document
            isExisting: true, // Mark as an existing file
          };
        }
      });
    
      setFiles(formattedFiles);
    }
  }, [existingFiles]);

  // Handle new file uploads
  const handleAcceptedFiles = (acceptedFiles: File[]) => {
 

    const newFiles = acceptedFiles.map((file) => ({
      preview: URL.createObjectURL(file), // Generate a preview URL
      name: file.name,
      type: file.type.startsWith("image/") ? "image" : "document", // Determine type based on MIME
      isExisting: false, // Mark as a newly uploaded file
      file, // Store the actual file object
    }));

    setFiles((prevFiles) => {
      if (shouldAppend) {
        // Keep existing files and replace newly added files
        const existingFiles = prevFiles.filter((file) => file.isExisting); // Keep only existing files
        return [...existingFiles, ...newFiles]; // Append new files to existing files
      } else {
        // Replace all files (existing and newly added)
        return newFiles;
      }
    });

    onFileUpload(acceptedFiles);
  };



  return (
    <React.Fragment>
      <Dropzone
        onDrop={(acceptedFiles) => handleAcceptedFiles(acceptedFiles)}
        multiple={multiple}
        accept={accept === "*" ? undefined : accept.split(",").reduce((acc, type) => {
          acc[type.trim()] = [];
          return acc;
        }, {} as Record<string, string[]>)} // Dynamically set accepted file types
      >
        {({ getRootProps, getInputProps }) => (
          <div className="dropzone dz-clickable text-center" {...getRootProps()}>
            <input {...getInputProps()} />
            <div className="dz-message needsclick">
              <div className="mb-3">
                <i className="display-4 text-muted ri-upload-cloud-2-fill" />
              </div>
              <p className="my-4">
                {multiple
                  ? "Drop files here to upload or click to select multiple files"
                  : "Drop a file here to upload or click to select a file"}
              </p>
            </div>
          </div>
        )}
      </Dropzone>
      <div className="list-unstyled mb-0" id="file-previews">
        {files.map((file, index) => (
          <div
            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
            key={index + "-file"}
          >
        
            <div className="p-2">
              <Row className="align-items-center">
                <Col className="col-auto me-auto">
                  {file?.type === "image" ? (
                    <img
                      src={file?.preview}
                      alt={file.name}
                      className="avatar-sm rounded bg-light img-fluid"
                    />
                  ) : (
                    <div className="d-flex align-items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="currentColor"
                        className="bi bi-file-earmark"
                        viewBox="0 0 16 16"
                      >
                        <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5z" />
                      </svg>
                      <span className="ms-2">{file.name}</span>
                    </div>
                  )}
                </Col>

                {/* Show download button only for existing files */}
                {file.isExisting && file.type === "document" && (
                  <Col className="col-auto">
                    <a href={file.preview} download={file.nameOriginal}>
                      <i className="ph-duotone ph-download fs-2" />
                    </a>
                  </Col>
                )}

                {/* Show delete button only for existing files */}
                {file.isExisting && deleteHandler && (
                  <Col className="col-auto">
                       <i className="ti ti-trash text-danger f-30 mouse-pointer" role="button" onClick={() => deleteHandler(file)}></i>
                  
                  </Col>
                )}
              </Row>
            </div>
          </div>
        ))}
      </div>
    </React.Fragment>
  );
};

export default FormDropzone;